import React, { useState, useRef, useEffect } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import ElementsDataService from '../../api/entitiesDataServices/ElementsDataService.js'
import AuthenticationService from '../general/AuthenticationService.js'

const T = {
    bg:'#f1f5f9', card:'#ffffff', border:'#e8edf5',
    blue:'#3b82f6', navy:'#0f172a',
    txt:'#1e293b', txt2:'#64748b', muted:'#94a3b8',
    green:'#16a34a', greenBg:'#f0fdf4', greenBdr:'#bbf7d0',
    red:'#dc2626', redBg:'#fef2f2', redBdr:'#fecaca',
    warn:'#f59e0b', warnBg:'#fffbeb', warnBdr:'#fde68a',
    r:16, rsm:10,
    shadow:'0 1px 3px rgba(15,23,42,.06)',
    font:"'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
}

function saveFile(formData) {
    const config = { headers: { 'Content-Type': 'multipart/form-data' } }
    return ElementsDataService.createElement('cfdis/declaracion', formData, config)
}

export default function DeclaracionProvisionalComponent() {
    const history  = useHistory()
    const { id }   = useParams()

    const [uploading, setUploading] = useState(false)
    const [results,   setResults]   = useState([])
    const [dragOver,  setDragOver]  = useState(false)
    const [files,     setFiles]     = useState([])
    const inputRef = useRef(null)

    useEffect(() => {
        if (!AuthenticationService.haveAuthorization('WRITE_CFDIS')) {
            history.push('/login')
        }
    }, []) // eslint-disable-line

    const handleFiles = e => {
        const selected = Array.from(e.target.files || [])
        setFiles(prev => {
            const existing = new Set(prev.map(f => f.name))
            return [...prev, ...selected.filter(f => !existing.has(f.name))]
        })
        setResults([])
    }

    const handleDrop = e => {
        e.preventDefault()
        setDragOver(false)
        const dropped = Array.from(e.dataTransfer.files)
        setFiles(prev => {
            const existing = new Set(prev.map(f => f.name))
            return [...prev, ...dropped.filter(f => !existing.has(f.name))]
        })
        setResults([])
    }

    const removeFile = name => setFiles(f => f.filter(x => x.name !== name))

    const handleSubmit = async () => {
        if (files.length === 0) return
        setUploading(true)
        setResults([])

        const newResults = []
        for (let i = 0; i < files.length; i++) {
            const file = files[i]
            try {
                const formData = new FormData()
                formData.append('declaracionProvisionalFile', file)
                const response = await saveFile(formData)
                const ok = response?.data?.message === 'Created'
                newResults.push({ name: file.name, ok })
            } catch (err) {
                newResults.push({ name: file.name, ok: false })
            }
            setResults([...newResults])
        }

        setUploading(false)
        if (newResults.every(r => r.ok)) setFiles([])
    }

    const successCount = results.filter(r => r.ok).length
    const failCount    = results.filter(r => !r.ok).length
    const done         = results.length === files.length && results.length > 0 && !uploading

    return (
        <div style={{ minHeight:'100vh', background:T.bg, fontFamily:T.font, padding:'24px 28px 64px' }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
                @keyframes spin { to { transform: rotate(360deg); } }
            `}</style>

            <div style={{ maxWidth:720, margin:'0 auto' }}>

                {/* Header */}
                <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:24 }}>
                    <button onClick={() => history.goBack()}
                        style={{ width:34, height:34, borderRadius:9, border:`1px solid ${T.border}`,
                            background:T.card, cursor:'pointer', fontSize:16, color:T.txt2,
                            display:'flex', alignItems:'center', justifyContent:'center',
                            boxShadow:T.shadow, flexShrink:0, fontFamily:T.font, transition:'all .15s' }}
                        onMouseEnter={e => { e.currentTarget.style.background=T.navy; e.currentTarget.style.color='white' }}
                        onMouseLeave={e => { e.currentTarget.style.background=T.card; e.currentTarget.style.color=T.txt2 }}>
                        ‹
                    </button>
                    <div>
                        <h1 style={{ margin:0, fontSize:22, fontWeight:800, color:T.navy, letterSpacing:'-0.025em' }}>
                            Cargar Declaraciones Provisionales
                        </h1>
                        <p style={{ margin:'3px 0 0', fontSize:13, color:T.txt2 }}>
                            Sube uno o varios archivos de declaración provisional
                        </p>
                    </div>
                </div>

                {/* Drop zone */}
                <div
                    onClick={() => inputRef.current?.click()}
                    onDragOver={e => { e.preventDefault(); setDragOver(true) }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleDrop}
                    style={{
                        background: dragOver ? `${T.blue}08` : T.card,
                        border:`2px dashed ${dragOver ? T.blue : T.border}`,
                        borderRadius:T.r, padding:'40px 24px', textAlign:'center',
                        cursor:'pointer', transition:'all .2s', marginBottom:16,
                        boxShadow: dragOver ? `0 0 0 4px ${T.blue}15` : T.shadow,
                    }}>
                    <div style={{ fontSize:36, marginBottom:10 }}>📎</div>
                    <p style={{ margin:0, fontSize:15, fontWeight:600, color:T.txt }}>
                        Arrastra archivos aquí o haz click para seleccionar
                    </p>
                    <p style={{ margin:'6px 0 0', fontSize:13, color:T.muted }}>
                        Múltiples archivos permitidos
                    </p>
                    <input ref={inputRef} id="fileToUpload" name="fileToUpload"
                        type="file" multiple onChange={handleFiles} style={{ display:'none' }} />
                </div>

                {/* Lista de archivos */}
                {files.length > 0 && (
                    <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:T.r, boxShadow:T.shadow, marginBottom:16, overflow:'hidden' }}>
                        <div style={{ padding:'12px 18px', borderBottom:`1px solid ${T.border}`, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                            <span style={{ fontSize:13, fontWeight:700, color:T.txt }}>
                                {files.length} archivo{files.length !== 1 ? 's' : ''} seleccionado{files.length !== 1 ? 's' : ''}
                            </span>
                            {!uploading && (
                                <button onClick={() => { setFiles([]); setResults([]) }}
                                    style={{ fontSize:12, color:T.muted, background:'none', border:'none', cursor:'pointer', fontFamily:T.font }}>
                                    Limpiar todo
                                </button>
                            )}
                        </div>
                        {files.map((file, i) => {
                            const result  = results.find(r => r.name === file.name)
                            const current = uploading && results.length === i
                            return (
                                <div key={file.name} style={{
                                    display:'flex', alignItems:'center', gap:12, padding:'10px 18px',
                                    borderBottom: i < files.length - 1 ? `1px solid ${T.border}` : 'none',
                                    background: result ? (result.ok ? T.greenBg : T.redBg) : 'transparent',
                                    transition:'background .3s'
                                }}>
                                    <span style={{ fontSize:16, flexShrink:0 }}>
                                        {current ? '⏳' : result?.ok ? '✅' : result ? '❌' : '📄'}
                                    </span>
                                    <span style={{ flex:1, fontSize:13, color:T.txt, fontWeight:500,
                                        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                        {file.name}
                                    </span>
                                    <span style={{ fontSize:12, color:T.muted, flexShrink:0 }}>
                                        {(file.size / 1024).toFixed(0)} KB
                                    </span>
                                    {result && (
                                        <span style={{ fontSize:12, fontWeight:600, flexShrink:0, color: result.ok ? T.green : T.red }}>
                                            {result.ok ? 'Cargado' : 'Error'}
                                        </span>
                                    )}
                                    {!uploading && !result && (
                                        <button onClick={() => removeFile(file.name)}
                                            style={{ background:'none', border:'none', cursor:'pointer',
                                                color:T.muted, fontSize:16, lineHeight:1, padding:'0 2px', fontFamily:T.font }}>
                                            ×
                                        </button>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                )}

                {/* Resumen final */}
                {done && (
                    <div style={{
                        padding:'14px 18px', borderRadius:T.rsm, marginBottom:16,
                        background: failCount === 0 ? T.greenBg : T.warnBg,
                        border:`1px solid ${failCount === 0 ? T.greenBdr : T.warnBdr}`,
                        display:'flex', alignItems:'center', gap:10
                    }}>
                        <span style={{ fontSize:18 }}>{failCount === 0 ? '🎉' : '⚠️'}</span>
                        <span style={{ fontSize:13, fontWeight:600, color: failCount === 0 ? T.green : T.warn }}>
                            {failCount === 0
                                ? `${successCount} archivo${successCount !== 1 ? 's' : ''} cargado${successCount !== 1 ? 's' : ''} con éxito`
                                : `${successCount} exitoso${successCount !== 1 ? 's' : ''}, ${failCount} con error`
                            }
                        </span>
                    </div>
                )}

                {/* Botón guardar */}
                <button
                    onClick={handleSubmit}
                    disabled={files.length === 0 || uploading}
                    style={{
                        width:'100%', padding:'13px 24px', borderRadius:T.rsm,
                        background: files.length === 0 || uploading ? T.muted : T.blue,
                        border:'none', color:'white', fontSize:14, fontWeight:700,
                        cursor: files.length === 0 || uploading ? 'not-allowed' : 'pointer',
                        fontFamily:T.font, transition:'all .15s',
                        display:'flex', alignItems:'center', justifyContent:'center', gap:8,
                        boxShadow: files.length > 0 && !uploading ? `0 4px 12px ${T.blue}40` : 'none',
                    }}>
                    {uploading ? (
                        <>
                            <span style={{ width:16, height:16, border:'2px solid #ffffff40',
                                borderTopColor:'white', borderRadius:'50%',
                                animation:'spin .7s linear infinite', display:'inline-block' }} />
                            Subiendo {results.length + 1} de {files.length}...
                        </>
                    ) : (
                        <>✅ Guardar {files.length > 0 ? `(${files.length} archivo${files.length !== 1 ? 's' : ''})` : ''}</>
                    )}
                </button>

            </div>
        </div>
    )
}
